import React from "react"
import { useSelector } from "react-redux"
import styled from "styled-components"

import {
    DATE_FORMAT,
    YESTERDAY, CURRENT_DAY, TOMORROW, THIS_WEEK, THIS_MONTH,
    LAST_7_DAYS, LAST_30_DAYS, LAST_60_DAYS, LAST_90_DAYS,
    EXACT_DATE, CUSTOM_RANGE,
} from "const"
import { DeadlineType } from "client/utils"
import { StoreType } from "client/store"
import {
    DateFormatter,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownItem,
    DatePicker,
} from "client/components"

interface DeadlineDivProps {
    deadline: Date | null
    overdue: boolean
}
export const DeadlineWrapper = styled.div<DeadlineDivProps>`
    width: fit-content;
    font-family: ${(props) => props.theme.openSans};
    font-size: 12px;
    padding: 0 10px;
    border-radius: 10px;
    background: ${(props) => (props.deadline
        ? (props.overdue ? props.theme.deadlineOverdueBackground : props.theme.deadlineBackground)
        : props.theme.deadlineEmptyBackground)};
    color: ${(props) => (props.deadline ? props.theme.deadlineColor : props.theme.deadlineEmptyColor)};
`


interface DeadlineProps extends React.HTMLAttributes<HTMLDivElement> {
    deadline: Date | null
}
export const Deadline = ({
    deadline,
}: DeadlineProps) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const nowDate = new Date()
    const nowTime = nowDate.getTime()

    const deadlineDate = deadline ? new Date(deadline) : null
    const deadlineTime = deadline ? deadlineDate.getTime() : null
    const overdue = deadline && (deadlineTime - nowTime) < 0

    let overdueTime: string
    if (overdue) {
        const secondTime = 1000 // milliseconds
        const minuteTime = 60 * secondTime
        const hourTime = 60 * minuteTime
        const dayTime = 24 * hourTime
        const inSeconds = Math.floor((nowTime - deadlineTime) / secondTime)
        const inMinutes = Math.floor((nowTime - deadlineTime) / minuteTime)
        const inHours = Math.floor((nowTime - deadlineTime) / hourTime)
        const inDays = Math.floor((nowTime - deadlineTime) / dayTime)
        const inWeeks = Math.floor((nowTime - deadlineTime) / (dayTime * 7))
        const inMonths = Math.floor((nowTime - deadlineTime) / (dayTime * 30))
        const inYears = Math.floor((nowTime - deadlineTime) / (dayTime * 365))

        if (inYears > 0) overdueTime = `- ${inYears} ${lang.years}`
        else if (inMonths > 0) overdueTime = `- ${inMonths} ${lang.months}`
        else if (inWeeks > 0) overdueTime = `- ${inWeeks} ${lang.weeks}`
        else if (inDays > 0) overdueTime = `- ${inDays} ${lang.days}`
        else if (inHours > 0) overdueTime = `- ${inHours} ${lang.hours}`
        else if (inMinutes > 0) overdueTime = `- ${inMinutes} ${lang.minutes}`
        else if (inSeconds > 0) overdueTime = `- ${inSeconds} ${lang.seconds}`
    }

    return (
        <DeadlineWrapper deadline={deadline} overdue={overdue}>
            {
                deadline === null
                && lang.noDeadline
            }
            {
                deadline !== null && !overdue
                && <DateFormatter date={deadline} defaultStyles />
            }
            {
                deadline !== null && overdue
                && overdueTime
            }
        </DeadlineWrapper>
    )
}


const deadlineArray = [
    YESTERDAY, CURRENT_DAY, TOMORROW, THIS_WEEK, THIS_MONTH,
    LAST_7_DAYS, LAST_30_DAYS, LAST_60_DAYS, LAST_90_DAYS,
    EXACT_DATE, CUSTOM_RANGE,
]
interface DeadlineFilterProps extends React.HTMLAttributes<HTMLDivElement> {
    deadline: DeadlineType
    exactDate: Date
    customRangeStart: Date
    customRangeEnd: Date
    setDeadline: (deadline: DeadlineType) => void
    setExactDate: (date: Date) => void
    setCustomRangeStart: (date: Date) => void
    setCustomRangeEnd: (date: Date) => void
}
export const DeadlineFilter = ({
    deadline,
    exactDate,
    customRangeStart,
    customRangeEnd,
    setDeadline,
    setExactDate,
    setCustomRangeStart,
    setCustomRangeEnd,
}: DeadlineFilterProps) => {
    const lang = useSelector((state: StoreType) => state.system.lang)
    return (
        <FormGroup>
            <Label>{lang.deadline}</Label>
            <FormGroup inline>
                <SelectDropdown
                    title={deadline ? lang[deadline] : lang.anyDate}
                    style={{ width: "100%" }}
                >
                    <SelectDropdownItem onClick={() => setDeadline(null)}>{lang.anyDate}</SelectDropdownItem>
                    {
                        deadlineArray.map((row: DeadlineType) => (
                            <SelectDropdownItem key={row} onClick={() => setDeadline(row)}>
                                {lang[row]}
                            </SelectDropdownItem>
                        ))
                    }
                </SelectDropdown>

                {
                    deadline === EXACT_DATE && (
                        <DatePicker
                            selected={exactDate}
                            onChange={(date: Date, e) => {
                                e.stopPropagation()
                                setExactDate(date)
                            }}
                            dateFormat={DATE_FORMAT}
                            isClearable
                        />
                    )
                }

                {
                    deadline === CUSTOM_RANGE && (
                        <>
                            <DatePicker
                                key="rangeStart"
                                selected={customRangeStart}
                                onChange={(date: Date, e) => {
                                    e.stopPropagation()
                                    setCustomRangeStart(date)
                                }}
                                dateFormat={DATE_FORMAT}
                                isClearable
                            />
                            <DatePicker
                                key="rangeEnd"
                                selected={customRangeEnd}
                                onChange={(date: Date, e) => {
                                    e.stopPropagation()
                                    setCustomRangeEnd(date)
                                }}
                                dateFormat={DATE_FORMAT}
                                isClearable
                            />
                        </>
                    )
                }
            </FormGroup>
        </FormGroup>
    )
}
